export const TECTOSILICATE_LOGIC = {
  description: "Tectosilicates (Framework Silicates) Identification Rules - Quartz, Feldspars, Feldspathoids, and Zeolites",
  
  coreRules: `
=== 硅酸盐之架状硅酸盐鉴定法则 (Tectosilicates / Framework Silicates Protocol) ===

这是地壳中丰度最高、统治着所有岩石圈（火成岩、沉积岩、变质岩）的绝对霸主。它们的硅氧四面体通过共享所有的氧原子，形成了一个三维的立体网络。这一章是矿物鉴定的“终极Boss战”。

[14.1 绝对的硬度与断口：石英家族 (The Quartz Family)]
石英 (Quartz) 是地壳中最常见的矿物之一。它的鉴定锚点是绝对的：
*   **绝杀物理锚点**: 莫氏硬度为 7 (轻松刻划玻璃和钢刀)。**没有解理 (No cleavage)**。
*   **视觉绝杀**: 当它破裂时，永远呈现典型的**贝壳状断口 (conchoidal fracture)**，就像敲碎一块厚玻璃的边缘。晶体如果完好，必是**六方柱状，顶端带有金字塔形的锥面**，晶面常常有横向条纹。
*   **图中展示**: 一块典型的无色透明/半透明的石英块体，展示出玻璃光泽和那种不规则的、带有贝壳状起伏的断口。

[14.2 统治地壳的双子星：长石家族 (The Feldspar Group)]
占据了地壳重量的近60%。所有的长石都有两组近乎正交（90度）或斜交的解理，硬度为 6（能划玻璃，但划不过石英）。它们被分为两大阵营：

*   **阵营 A: 碱性长石 (Alkali Feldspars / K-Feldspars)** (富钾钠)
    *   **正长石 (Orthoclase)**: 单斜晶系。**解理面夹角绝对等于 90° (Right angle)**。常见的肉红色、粉红色到白色/灰色块体。常常呈现卡斯巴双晶（晶体中间像被一刀切开，两边反光不同）。
    *   **微斜长石 (Microcline)**: 三斜晶系。**解理面夹角接近 90° (89°30')**。
        *   **天河石变种 (Amazonite)**: 你的图里最亮眼的一个！寻找**极其独特的蓝绿色、知更鸟蛋蓝 (teal, blue-green, robin's egg blue)**，且常常带有白色网状的钠长石条纹。
    *   **透长石 (Sanidine)**: 高温斑晶，常见于流纹岩这种酸性火山岩中，常常是无色透明到白色的板状晶体。
    *   **条纹长石 (Perthite)**: 当钾长石和钠长石在冷却时发生离溶，会在钾长石底板上析出肉眼可见的**不规则的平行/交错的钠长石白色条带或线状纹理 (exsolution lamellae)**，这是它的绝杀特征。

*   **阵营 B: 斜长石系列 (Plagioclase Feldspars)** (富钠钙连续固溶体)
    *   **绝杀锚点**: 所有的斜长石在它的一个解理面上，用放大镜总能看到**平行的、像头发丝一样细密的“聚片双晶纹” (polysynthetic twinning striations)**。这是区分它们和碱性长石的终极法则。
    *   **钠长石 (Albite)**: 端员矿物，纯白色，像白糖块或者瓷器。
    *   **奥长石 (Oligoclase)**: 包括著名的**日长石 (Sunstone)**。寻找在红褐色/橙色长石基底上，因为内部含有赤铁矿或自然铜薄片而闪烁着**金色/红色闪光 (aventurescence/schiller effect)** 的标本。
    *   **中长石 (Andesine)** & **拉长石 (Labradorite)**:
        *   **拉长石绝杀**: 当你在不同角度转动一块灰黑色/暗色的拉长石时，它会突然闪现出**极其绚丽的蓝色、绿色、黄色甚至紫色的游彩/晕彩 (Labradorescence/Schiller effect)**，如同极光一般。你的拉长石配图完美展示了带有蓝绿色变彩的解理面。
    *   **培长石 (Bytownite)** & **钙长石 (Anorthite)**: 基性岩（辉长岩、玄武岩）中的常客，颜色偏深灰到带浅绿灰色。图片展示了典型的暗色调中偏绿的钙长石标本。

*   **歪长石 (Anorthoclase)**: 介于碱性长石和斜长石之间的特殊高温变种。外观通常呈致密的暗灰色、黑褐色，常见于富钠的火山岩中（如图所示）。

[14.3 长石的“营养不良”亲戚：似长石族 (Feldspathoids)]
这是一群“生不逢时”的矿物。它们生长在二氧化硅极度匮乏的碱性岩浆里。**只要岩石里有原生的石英，就绝对不可能出现似长石。**

*   **霞石 (Nepheline)**: 看起来像石英或者褪色发灰的长石，但它**带有强烈的油脂光泽 (greasy luster)**，比石英软（硬度5.5-6），且遇酸会胶凝化。图中的块体呈现灰白色带点微小孔洞和暗色杂质。
*   **白榴石 (Leucite)**: 维苏威火山岩石的标志。寻找在灰色火山岩基质中，凸显出来的**极其完美的灰白色多面体晶体 (typically forms perfect trapezohedrons，像一个个小多边形足球)**。
*   **青金石 (Lapis Lazuli / Lazurite)**: 千年帝王之色。
    *   **绝杀锚点**: 它是极其浓郁的**深蓝色、群青色 (deep blue, ultramarine)**。且必定与**金色的黄铁矿 (Pyrite)**点缀、以及**白色的方解石 (Calcite)**脉络共生。这是区分它与蓝铜矿、方钠石的最核心依据。
*   **方钠石 (Sodalite)**: 也是蓝色调。
    *   **视觉锚点**: 它的蓝色通常带有一点紫调或者更“粉”一点的蓝，并且往往伴随**大量的白色脉带或斑块 (often with distinct white veining or mottling)**，**且通常没有金色的黄铁矿伴生**。（如图所示典型的深蓝带白纹理）。

[14.4 水的禁锢者与岩石空洞的精灵：沸石族 (The Zeolite Group)]
这是一群包含着极多水分子的空旷骨架结构矿物。它们几乎总是生长在**玄武岩、安山岩的杏仁体（气孔）或热液裂隙中**。它们常常是白色、肉色或无色的结晶簇。遇到酸容易胶凝化或溶解。由于它们长得极其相似，这几十种沸石在野外极难靠肉眼完美区分，往往需要借助晶体形态的细微差别：

*   **方沸石 (Analcime)**: 长得像白榴石！寻找在玄武岩空洞中，纯白色或无色的、呈完美的**四角三八面体 (like a faceted soccer ball, similar to leucite but in a different environment)** 或立方形状的闪亮晶体体。
*   **菱沸石 (Chabazite)**: 寻找看起来像一个个稍微倾斜/扭曲的立方体（**菱面体，Pseudo-cubic/Rhombohedral crystals**）簇生在一起的晶体，颜色从白色、黄色到肉红色都有。
*   **辉沸石 (Stilbite)**: 绝杀形态：**束状晶体 (sheaf-like)**！它们倾向于长成两头宽中间窄的**像捆扁平的麦束、领结状 (bowtie shape)**。通常是白色、或者有极吸引人的肉粉色、鲑鱼红色。图中的粉色结晶展示了它的经典颜色。
*   **片沸石 (Heulandite)**: 常常和辉沸石长在一起。寻找具有**珍珠光泽 (pearly luster)** 的侧面，晶体呈现明显的**“棺材状” (coffin-shaped)** 面貌或楔形块状。
*   **钠沸石 (Natrolite)**: 长成极其细长的**针状、毛发状、放射状结晶簇 (acicular, radiating needles or fibrous masses)**，像白色的海胆或小绒球。常常填满整个玄武岩空洞。
*   **重十字石/交沸石 (Phillipsite)**: 常呈现白/淡黄色。标志性特征是其常常形成穿透双晶，外观看似微小的十字形或复杂的假斜方结构，结晶极其微小紧密。
*   **菱灰沸石/浊沸石 (Laumontite)**: 这种沸石一脱水就会变成白色的粉末或者变得极脆（粉化现象）。原状一般呈白色棱柱状。图中展示了它像碎骨头一样的白色聚集体。
*   **杆沸石 (Thomsonite)** / **毛沸石 (Erionite)** 等：通常是在玄武岩空洞中呈现放射状、球状或者复杂的纤维状集合体。你的图里完美展示了多气孔玄武岩中充填的各种白色/带有放射纹理的沸石小球（杏仁构造）。
*   **赛黄晶 (Danburite)**：虽然名字像黄玉，但它是硼硅酸盐。这里也放在架状处理。看起来极其像黄玉或石英。
    *   **鉴定锚点**: 它的晶体**顶端是楔形的 (wedge-shaped terminations)**，而不是石英那种锥形尖端。且它的硬度为7，解理不明显，常在矽卡岩中出现。
`
};
